"use strict";

exports.__esModule = true;
exports.DeviceCard = DeviceCard;
exports.fitStats = fitStats;
exports.stateLineFits = stateLineFits;
var _react = _interopRequireWildcard(require("react"));
var _styledComponents = _interopRequireWildcard(require("styled-components"));
var _polished = require("polished");
var _DeviceColorController = require("../../Device/DeviceColorSources/DeviceColorController");
var _DeviceState = require("./DeviceState");
var _stateEdge = require("../common/stateEdge");
var _Timer = require("../../Time/Timer");
var _Utils = require("../../../Utils");
var _TileContext = require("../common/TileContext");
var _guessRelativeTextSize = require("../../Measures/guessRelativeTextSize");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
// The device card from the next-gen heat map: a state-coloured edge along the
// top, the device name, a line naming the current state and how long it has
// held, and the KPIs as a row of labelled stats along the bottom. The current
// activity joins the state line when it fits and takes a line of its own when
// it would not, so the state and the duration are never cut short. The stats
// take whatever height the card has to spare: a dense grid keeps them as small
// mono lines, a roomy card grows them into bold headline figures.
var PAD_X = 16;
var PAD_TOP = 6;
var PAD_BOTTOM = 6;
var TITLE_HEIGHT = 22;
var SUBTITLE_HEIGHT = 16;
var STATE_HEIGHT = 18;
var DETAIL_HEIGHT = 16;
var BLOCK_GAP = 4;
var STAT_GAP = 14;
var STAT_ROW_GAP = 4;
var STAT_MIN_WIDTH = 64;
// A single row of narrower stats beats two rows of stats too short to read.
var STAT_NARROW_WIDTH = 52;
var STAT_MIN_HEIGHT = 28;
// Tall enough for the tile's largest value (48px) over a scaled label.
var STAT_MAX_HEIGHT = 84;
// From this height on a stat is drawn as a bold headline figure.
var STAT_PROMINENT_HEIGHT = 44;
var NEUTRAL_ACCENT = "#D5DDE2";

// Which line of the card a template child becomes. A template holding
// anything else (a chart, a table) keeps its configured grid positions.
var TITLE_TYPES = ["DeviceProperty"];
var STATE_TYPES = ["TimelineValue", "AdvancedTimelineValue"];
var STAT_TYPES = ["MetricValue", "MetricValueAggregate", "PerformanceParameterValue", "Clock"];
function roleOf(child) {
  if (TITLE_TYPES.includes(child.type)) return "title";
  if (STATE_TYPES.includes(child.type)) return "state";
  if (STAT_TYPES.includes(child.type)) return "stat";
  return undefined;
}

// A metric shown with its target is two stats side by side.
var slotsOf = function slotsOf(child) {
  return child.props && child.props.showTarget ? 2 : 1;
};

// How many rows the slots take when no stat may be narrower than minWidth,
// and how wide and tall each stat is then.
function layoutRows(_ref) {
  var slots = _ref.slots,
    innerWidth = _ref.innerWidth,
    room = _ref.room,
    minWidth = _ref.minWidth;
  var perRowMax = Math.max(1, Math.floor((innerWidth + STAT_GAP) / (minWidth + STAT_GAP)));
  var rows = Math.ceil(slots / Math.min(perRowMax, slots));
  var perRow = Math.ceil(slots / rows);
  var statWidth = Math.max(minWidth, Math.floor((innerWidth - STAT_GAP * (perRow - 1)) / perRow));
  var rowHeight = Math.floor((room - STAT_ROW_GAP * (rows - 1)) / rows);
  return {
    rows: rows,
    statWidth: statWidth,
    rowHeight: rowHeight
  };
}

// Lay the stats out in as few rows as fit the width, balanced, and give each
// row an equal share of the height left under the header and state line.
// When that leaves the rows too short to read, a single row of narrower
// stats is tried instead.
function fitStats(_ref2) {
  var slots = _ref2.slots,
    innerWidth = _ref2.innerWidth,
    room = _ref2.room;
  if (slots === 0) {
    return {
      statWidth: 0,
      statHeight: 0,
      prominent: false
    };
  }
  var layout = layoutRows({
    slots: slots,
    innerWidth: innerWidth,
    room: room,
    minWidth: STAT_MIN_WIDTH
  });
  if (layout.rows > 1 && layout.rowHeight < STAT_MIN_HEIGHT) {
    var narrow = layoutRows({
      slots: slots,
      innerWidth: innerWidth,
      room: room,
      minWidth: STAT_NARROW_WIDTH
    });
    if (narrow.rows < layout.rows) {
      layout = narrow;
    }
  }
  var _layout = layout,
    statWidth = _layout.statWidth,
    rowHeight = _layout.rowHeight;
  var statHeight = Math.max(STAT_MIN_HEIGHT, Math.min(STAT_MAX_HEIGHT, rowHeight));
  return {
    statWidth: statWidth,
    statHeight: statHeight,
    prominent: statHeight >= STAT_PROMINENT_HEIGHT
  };
}

// Text width in a CSS font: a canvas measures it exactly; without one (tests)
// a character-count estimate stands in.
var canvasContext;
function measureText(text, font) {
  if (canvasContext === undefined) {
    var canvas = typeof document !== "undefined" ? document.createElement("canvas") : null;
    canvasContext = canvas && canvas.getContext && canvas.getContext("2d") || null;
  }
  if (canvasContext) {
    canvasContext.font = font;
    return canvasContext.measureText(text).width;
  }
  var size = parseFloat(font.match(/(\d+(\.\d+)?)px/)[1]) || 12;
  return (0, _guessRelativeTextSize.guessRelativeTextSize)(text) * size * 0.62;
}
var DOT_SIZE = 8;
var LINE_GAP = 7;
var STATE_FONT_SIZE = 12.5;
var SINCE_FONT_SIZE = 11.5;

/**
 * Whether the state, its activity and the duration fit one line of the card.
 */
function stateLineFits(_ref3) {
  var stateName = _ref3.stateName,
    activityName = _ref3.activityName,
    duration = _ref3.duration,
    innerWidth = _ref3.innerWidth,
    _ref3$sansFont = _ref3.sansFont,
    sansFont = _ref3$sansFont === void 0 ? "sans-serif" : _ref3$sansFont,
    _ref3$measure = _ref3.measure,
    measure = _ref3$measure === void 0 ? measureText : _ref3$measure;
  var sans = function sans(weight) {
    return weight + " " + STATE_FONT_SIZE + "px " + sansFont;
  };
  var state = measure(stateName || "", sans(600));
  var activity = activityName ? measure("\xB7 " + activityName, sans(500)) + LINE_GAP : 0;
  // The duration grows over time; allow for a typical "for 00h 00m".
  var since = duration ? Math.max(measure("for " + duration, SINCE_FONT_SIZE + "px " + _TileContext.FLAT_FONT_MONO), measure("for 00h 00m", SINCE_FONT_SIZE + "px " + _TileContext.FLAT_FONT_MONO)) + LINE_GAP : 0;
  return DOT_SIZE + LINE_GAP + state + activity + since <= innerWidth;
}
var safeMix = function safeMix(amount, color, base) {
  try {
    return (0, _polished.mix)(amount, color, base);
  } catch (e) {
    return color;
  }
};
var Card = _styledComponents["default"].div.withConfig({
  displayName: "DeviceCard__Card",
  componentId: "sc-lj20ls-0"
})(["position:relative;box-sizing:border-box;overflow:hidden;color:#1b2227;"]);
var Accent = _styledComponents["default"].div.withConfig({
  displayName: "DeviceCard__Accent",
  componentId: "sc-lj20ls-1"
})(["position:absolute;top:0;left:0;right:0;transition:background 0.4s ease;"]);
var Body = _styledComponents["default"].div.withConfig({
  displayName: "DeviceCard__Body",
  componentId: "sc-lj20ls-2"
})(["position:absolute;left:0;right:0;bottom:0;display:flex;flex-direction:column;box-sizing:border-box;min-height:0;padding:", "px ", "px ", "px;"], PAD_TOP, PAD_X, PAD_BOTTOM);
var Header = _styledComponents["default"].div.withConfig({
  displayName: "DeviceCard__Header",
  componentId: "sc-lj20ls-3"
})(["flex:none;"]);
var StateLine = _styledComponents["default"].div.withConfig({
  displayName: "DeviceCard__StateLine",
  componentId: "sc-lj20ls-4"
})(["flex:none;display:flex;align-items:center;gap:", "px;height:", "px;margin-top:", "px;min-width:0;overflow:hidden;"], LINE_GAP, STATE_HEIGHT, BLOCK_GAP);
var Dot = _styledComponents["default"].span.withConfig({
  displayName: "DeviceCard__Dot",
  componentId: "sc-lj20ls-5"
})(["flex:none;width:", "px;height:", "px;border-radius:50%;"], DOT_SIZE, DOT_SIZE);
var StateText = _styledComponents["default"].span.withConfig({
  displayName: "DeviceCard__StateText",
  componentId: "sc-lj20ls-6"
})(["flex:none;font-size:", "px;font-weight:600;white-space:nowrap;"], STATE_FONT_SIZE);

// The activity beside the state; the only part of the line that may be cut.
var Activity = _styledComponents["default"].span.withConfig({
  displayName: "DeviceCard__Activity",
  componentId: "sc-lj20ls-7"
})(["flex:0 1 auto;min-width:0;font-size:", "px;font-weight:500;color:#5d6b75;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"], STATE_FONT_SIZE);
var Since = _styledComponents["default"].span.withConfig({
  displayName: "DeviceCard__Since",
  componentId: "sc-lj20ls-8"
})(["flex:none;font-family:", ";font-size:", "px;color:#7c8a94;white-space:nowrap;"], _TileContext.FLAT_FONT_MONO, SINCE_FONT_SIZE);

// The activity under the state when the line has no room for it, indented
// past the dot.
var Detail = _styledComponents["default"].div.withConfig({
  displayName: "DeviceCard__Detail",
  componentId: "sc-lj20ls-9"
})(["flex:none;height:", "px;line-height:", "px;padding-left:", "px;font-size:12px;color:#5d6b75;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"], DETAIL_HEIGHT, DETAIL_HEIGHT, DOT_SIZE + LINE_GAP);
var Stats = _styledComponents["default"].div.withConfig({
  displayName: "DeviceCard__Stats",
  componentId: "sc-lj20ls-10"
})(["flex:none;margin-top:auto;padding-top:", "px;display:flex;flex-wrap:wrap;align-items:flex-end;gap:", "px ", "px;"], BLOCK_GAP, STAT_ROW_GAP, STAT_GAP);

/**
 * The device's current state for the card. A colour source configured on the
 * grid still decides the accent colour.
 */
function CardState(_ref4) {
  var deviceUuid = _ref4.deviceUuid,
    colorProps = _ref4.colorProps,
    children = _ref4.children;
  return /*#__PURE__*/_react["default"].createElement(_DeviceState.DeviceStateSubscription, {
    deviceUuid: deviceUuid
  }, function (state) {
    if (colorProps.backgroundColorSource == null) {
      return children(state);
    }
    return /*#__PURE__*/_react["default"].createElement(_DeviceColorController.DeviceColorController, _extends({
      deviceUuid: deviceUuid
    }, colorProps), function (_ref5) {
      var backgroundColor = _ref5.backgroundColor;
      return children(Object.assign({}, state, {
        color: backgroundColor || state.color
      }));
    });
  });
}
function renderDesigned(_ref6) {
  var elements = _ref6.elements,
    width = _ref6.width,
    height = _ref6.height,
    accentHeight = _ref6.accentHeight,
    render = _ref6.render,
    accent = _ref6.accent,
    stateName = _ref6.stateName,
    activityName = _ref6.activityName,
    startMillis = _ref6.startMillis,
    sansFont = _ref6.sansFont;
  var indexed = elements.map(function (child, index) {
    return {
      child: child,
      index: index,
      role: roleOf(child)
    };
  });
  var titles = indexed.filter(function (item) {
    return item.role === "title";
  });
  var states = indexed.filter(function (item) {
    return item.role === "state";
  });
  var stats = indexed.filter(function (item) {
    return item.role === "stat";
  });
  var innerWidth = Math.max(0, width - 2 * PAD_X);
  var headerHeight = titles.length ? TITLE_HEIGHT + (titles.length - 1) * SUBTITLE_HEIGHT : 0;
  var stateRows = states.length ? states.length : stateName ? 1 : 0;
  var duration = startMillis > 0 ? (0, _Utils.formatDuration)(Math.max(0, Date.now() - startMillis)) : undefined;
  var inlineActivity = states.length === 0 && Boolean(stateName && activityName) && stateLineFits({
    stateName: stateName,
    activityName: activityName,
    duration: duration,
    innerWidth: innerWidth,
    sansFont: sansFont
  });
  var detailRows = states.length === 0 && stateName && activityName && !inlineActivity ? 1 : 0;
  var stateHeight = stateRows ? stateRows * STATE_HEIGHT + detailRows * DETAIL_HEIGHT + BLOCK_GAP : 0;
  var room = height - accentHeight - PAD_TOP - PAD_BOTTOM - headerHeight - stateHeight - (stats.length ? BLOCK_GAP : 0);
  var slots = stats.reduce(function (total, item) {
    return total + slotsOf(item.child);
  }, 0);
  var _fitStats = fitStats({
      slots: slots,
      innerWidth: innerWidth,
      room: room
    }),
    statWidth = _fitStats.statWidth,
    statHeight = _fitStats.statHeight,
    prominent = _fitStats.prominent;
  var renderFlat = function renderFlat(_ref7, role, w, h) {
    var child = _ref7.child,
      index = _ref7.index;
    return /*#__PURE__*/_react["default"].createElement(_TileContext.TileContext.Provider, {
      key: index,
      value: {
        flat: true,
        role: role,
        prominent: role === "stat" && prominent
      }
    }, render(Object.assign({}, child, {
      props: Object.assign({}, child.props, {
        x: 0,
        y: 0,
        width: w,
        height: h
      })
    }), index));
  };
  return /*#__PURE__*/_react["default"].createElement(Body, {
    style: {
      top: accentHeight
    }
  }, titles.length > 0 && /*#__PURE__*/_react["default"].createElement(Header, null, titles.map(function (item, i) {
    return renderFlat(item, i === 0 ? "title" : "subtitle", innerWidth, i === 0 ? TITLE_HEIGHT : SUBTITLE_HEIGHT);
  })), states.length > 0 ? states.map(function (item, i) {
    return /*#__PURE__*/_react["default"].createElement("div", {
      key: item.index,
      style: {
        flex: "none",
        marginTop: i === 0 ? BLOCK_GAP : 0
      }
    }, renderFlat(item, "state", innerWidth, STATE_HEIGHT));
  }) : stateName && /*#__PURE__*/_react["default"].createElement(_react["default"].Fragment, null, /*#__PURE__*/_react["default"].createElement(StateLine, null, /*#__PURE__*/_react["default"].createElement(Dot, {
    style: {
      background: accent
    }
  }), /*#__PURE__*/_react["default"].createElement(StateText, {
    style: {
      color: safeMix(0.35, "#1B2227", accent)
    }
  }, stateName), inlineActivity && /*#__PURE__*/_react["default"].createElement(Activity, {
    title: activityName
  }, "\xB7 ", activityName), startMillis > 0 && /*#__PURE__*/_react["default"].createElement(_Timer.Timer, {
    interval: 30000
  }, function (_ref8) {
    var now = _ref8.now;
    return /*#__PURE__*/_react["default"].createElement(Since, null, "for ", (0, _Utils.formatDuration)(Math.max(0, now - startMillis)));
  })), activityName && !inlineActivity && /*#__PURE__*/_react["default"].createElement(Detail, {
    title: activityName
  }, activityName)), stats.length > 0 && /*#__PURE__*/_react["default"].createElement(Stats, null, stats.map(function (item) {
    return renderFlat(item, "stat", statWidth * slotsOf(item.child) + STAT_GAP * (slotsOf(item.child) - 1), statHeight);
  })));
}

/**
 * A Grid rendered as a device card. Templates made only of value tiles get the
 * designed layout; anything else keeps its grid positions under the accent.
 */
function DeviceCard(props) {
  var width = props.width,
    height = props.height,
    _props$elements = props.elements,
    elements = _props$elements === void 0 ? [] : _props$elements,
    device = props.device,
    colorProps = props.colorProps,
    interactionProps = props.interactionProps,
    testid = props.testid,
    render = props.render,
    renderChild = props.renderChild;
  var designed = elements.length > 0 && elements.every(function (child) {
    return roleOf(child) != null;
  });
  var theme = (0, _react.useContext)(_styledComponents.ThemeContext) || {};
  var sansFont = theme.FontFamilySans || "sans-serif";
  return /*#__PURE__*/_react["default"].createElement(CardState, {
    deviceUuid: device.uuid,
    colorProps: colorProps
  }, function (_ref9) {
    var text = _ref9.text,
      stateName = _ref9.stateName,
      activityName = _ref9.activityName,
      color = _ref9.color,
      startMillis = _ref9.startMillis;
    var accent = color && color !== "transparent" ? color : NEUTRAL_ACCENT;
    var accentHeight = (0, _stateEdge.stateEdgeSize)(width);
    return /*#__PURE__*/_react["default"].createElement(Card, _extends({
      "data-testid": testid,
      style: {
        width: width,
        height: height
      }
    }, interactionProps), /*#__PURE__*/_react["default"].createElement(Accent, {
      style: {
        background: accent,
        height: accentHeight
      }
    }), designed ? renderDesigned({
      elements: elements,
      width: width,
      height: height,
      accentHeight: accentHeight,
      render: render,
      accent: accent,
      stateName: stateName || text,
      activityName: activityName,
      startMillis: startMillis,
      sansFont: sansFont
    }) : elements.map(function (child, index) {
      return (
        /*#__PURE__*/
        // Mixed templates keep their cells, but inside the card
        // every tile is still a flat line, and charts plain bands.
        _react["default"].createElement(_TileContext.TileContext.Provider, {
          key: index,
          value: {
            flat: true,
            role: roleOf(child) || "value"
          }
        }, renderChild(child, index))
      );
    }));
  });
}